<template>
   <b-modal
    ref="modal"
    :title="modalTitle"
    :hide-header-close="!modalClosable"
    :no-close-on-backdrop="!modalClosable"
    :no-close-on-esc="!modalClosable"
    :size="modalSize"
    modal-class="einvoice-draft-modal"
    @hide="onModalHide"
    @hidden="onModalHidden"
    > <!-- ─── Custom Header ──────────────────────────────────── --> <template
      #modal-header="{ close }"
      >
      <div class="draft-modal-header w-100">

        <div class="d-flex align-items-center justify-content-between mb-3">

          <h5 class="mb-0 draft-modal-title">
             <i class="fas fa-file-invoice mr-2"></i> {{ modalTitle }}
          </h5>
           <button
            v-if="modalClosable"
            type="button"
            class="close"
            aria-label="Đóng"
            @click="close()"
          >
             <span aria-hidden="true">&times;</span> </button
          >
        </div>
         <!-- Stepper -->
        <div
          class="draft-stepper"
          role="progressbar"
          :aria-valuenow="currentStepIndex + 1"
          aria-valuemin="1"
          aria-valuemax="3"
        >

          <div
            v-for="(step, idx) in steps"
            :key="step.key"
            class="draft-step"
            :class="{
              'is-active': idx === currentStepIndex,
              'is-completed': idx < currentStepIndex,
              'is-upcoming': idx > currentStepIndex,
            }"
          >

            <div class="step-indicator">
               <i
                v-if="idx < currentStepIndex"
                class="fas fa-check"
              ></i
              > <span v-else>{{ idx + 1 }}</span
              >
            </div>
             <span class="step-label">{{ step.label }}</span
            >
            <div
              v-if="idx < steps.length - 1"
              class="step-connector"
            />

          </div>

        </div>

      </div>
       </template
    > <!-- ─── Loading State ────────────────────────────────────── -->
    <div
      v-if="isLoadingState"
      class="draft-step-content processing-state"
    >

      <div class="processing-visual">

        <div class="processing-icon-wrapper">
           <b-spinner
            variant="primary"
            style="width: 2.5rem; height: 2.5rem"
          />
        </div>

        <h6 class="processing-title mt-3"> {{ loadingStateTitle }} </h6>

        <p class="processing-subtitle"> {{ loadingStateSubtitle }} </p>

      </div>

    </div>
     <!-- ─── Step 1: BUYER_INFO ────────────────────────────── -->
    <div
      v-else-if="modalState === 'BUYER_INFO'"
      class="draft-step-content"
    >
       <!-- Bill Info Fetch Error Banner --> <b-alert
        v-if="billInfoFetchError"
        show
        variant="warning"
        dismissible
        class="mb-3"
        @dismissed="billInfoFetchError = null"
        > <i class="fas fa-exclamation-triangle mr-1"></i> {{ billInfoFetchError }} <span
          class="d-block small mt-1"
          >Bạn có thể nhập thủ công thông tin khách hàng.</span
        > </b-alert
      > <!-- 1. Top Warnings --> <selection-warning-banner
        v-if="isBulkBatch"
        :affected-bills="affectedBills"
        class="mb-3"
      /> <batch-size-warning
        v-if="isBulkBatch"
        :count="operation.billIds.length"
        :threshold="batchWarnThreshold"
        class="mb-3"
      /> <!-- 2. Context Summary -->
      <div class="confirm-summary-bar mb-3">

        <div class="summary-row-basic">

          <div class="summary-item">
             <i class="fas fa-file-alt text-primary"></i>
            <div>
               <span class="summary-value">{{ operation.billIds.length }}</span
              > <span class="summary-label">hóa đơn được chọn</span>
            </div>

          </div>

          <div class="summary-divider" />

          <div class="summary-item">
             <i :class="saleTypeIcon"></i>
            <div>
               <span class="summary-value">{{ saleTypeLabel }}</span
              > <span class="summary-label">loại hóa đơn</span>
            </div>

          </div>

          <div
            v-if="operation.billIds.length > 0"
            class="ml-auto summary-action"
          >
             <b-button
              size="sm"
              variant="link"
              class="text-decoration-none font-weight-bold"
              v-b-toggle.bill-list-collapse
              > Xem danh sách <i class="fas fa-chevron-down ml-1"></i> </b-button
            >
          </div>

        </div>

      </div>
       <!-- 3. Collapsible Bill List --> <b-collapse
        id="bill-list-collapse"
        class="mb-4"
        >
        <div class="confirm-bills-list">

          <div class="bills-scroll-area">
            <div
              v-for="(bill, idx) in previewBills"
              :key="bill.billId"
              class="bill-preview-item"
            >
               <span class="bill-index">{{ idx + 1 }}</span
              > <router-link
                v-if="bill.detailRoute"
                :to="bill.detailRoute"
                class="bill-code bill-code-link"
                target="_blank"
                rel="noopener noreferrer"
                :title="`Mở chi tiết hóa đơn ${bill.displayCode}`"
                >{{ bill.displayCode }}</router-link
              > <span
                v-else
                class="bill-code"
                >{{ bill.displayCode }}</span
              >
            </div>

            <div
              v-if="operation.billIds.length > previewLimit"
              class="bill-preview-more"
            >
               <i class="fas fa-ellipsis-h mr-1"></i> và {{
                operation.billIds.length - previewLimit
              }} hóa đơn khác
            </div>

          </div>

        </div>
         </b-collapse
      > <!-- 4. Bulk Mode Toggle --> <bulk-input-mode-toggle
        v-if="isBulkBatch"
        v-model="bulkMode"
        :bill-count="operation.billIds.length"
        :detected-mode="detectedMode"
        :has-unsaved-data="hasUnsavedBulkData"
        class="mb-4"
      /> <!-- 5. Buyer Info Section -->
      <div class="buyer-info-section mt-3">
         <label
          v-if="!isBulkBatch"
          class="section-label"
          >Thông tin người mua</label
        > <shared-buyer-form
          v-if="!isBulkBatch || bulkMode === 'SHARED'"
          ref="sharedBuyerForm"
          :buyer-type="buyerType"
          :field-states="computedFieldStates"
          :overrides="fieldOverrides"
          :is-wholesale="operation.laneSaleType === 'WHOLESALE'"
          @update:buyerType="setBuyerType($event)"
          @update:overrides="onOverridesUpdate"
          @validation="onSharedFormValidation"
        /> <bulk-editor-grid
          v-if="isBulkBatch && bulkMode === 'PER_BILL'"
          ref="bulkGrid"
          v-model="gridRows"
        />
      </div>
       <!-- Preflight warnings -->
      <div
        v-if="preflightWarnings.length"
        class="confirm-warnings mt-2"
      >

        <div
          v-for="(warn, idx) in preflightWarnings"
          :key="idx"
          class="warning-item"
          :class="`severity-${warn.severity}`"
        >
           <i
            :class="warn.severity === 'hard' ? 'fas fa-exclamation-circle' : 'fas fa-info-circle'"
          ></i
          > <span>{{ warn.message }}</span
          >
        </div>

      </div>
       <!-- Buyer Validation Errors Summary -->
      <div
        v-if="hasBuyerErrors && buyerTouched"
        class="confirm-warnings mt-2"
      >

        <div class="warning-item severity-hard">
           <i class="fas fa-exclamation-circle"></i> <span
            >Vui lòng hoàn tất thông tin người mua trước khi tiếp tục</span
          >
        </div>

      </div>

    </div>
     <!-- ─── Step 2: VAT_PREVIEW ────────────────────────────── -->
    <div
      v-else-if="modalState === 'VAT_PREVIEW'"
      class="draft-step-content"
    >
       <vat-preview-section
        :invoices="previewInvoices"
        :sale-type="operation.laneSaleType"
        :auto-fetch="true"
        @preview-loaded="onPreviewLoaded"
        @preview-error="onPreviewError"
      />
    </div>
     <!-- ─── Step 3: Processing State (VALIDATING / SUBMITTING) ───── -->
    <div
      v-else-if="isProcessingState"
      class="draft-step-content processing-state"
    >

      <div class="processing-visual">

        <div class="processing-icon-wrapper">
           <b-spinner
            variant="primary"
            style="width: 2.5rem; height: 2.5rem"
          />
        </div>

        <h6 class="processing-title mt-3">
           {{ modalState === 'VALIDATING' ? 'Đang kiểm tra dữ liệu…' : 'Đang tạo bản nháp…' }}
        </h6>

        <p class="processing-subtitle">
           {{
            modalState === 'VALIDATING'
              ? 'Hệ thống đang xác minh tính hợp lệ của các hóa đơn.'
              : 'Hệ thống đang xử lý và tạo bản nháp HĐĐT.'
          }}
        </p>

      </div>

      <div
        v-if="jobProgress.total > 0"
        class="processing-progress"
      >

        <div class="progress-info">
           <span>Tiến trình</span> <span class="progress-count"
            >{{ jobProgress.current }}/{{ jobProgress.total }}</span
          >
        </div>
         <b-progress
          :value="jobProgress.current"
          :max="jobProgress.total"
          variant="primary"
          height="8px"
          class="mt-1"
          :animated="true"
          :striped="true"
        />
        <div
          v-if="progressPercent > 0"
          class="progress-percent mt-1"
        >
           {{ progressPercent }}%
        </div>

      </div>

      <div
        v-if="isBatch"
        class="processing-hint"
      >
         <i class="fas fa-info-circle mr-1"></i> Nếu bạn đóng tab, tiến trình vẫn chạy nền. Kết quả
        sẽ hiển thị trong danh sách nháp.
      </div>

    </div>
     <!-- ─── Step 3: Result State (RESULT / ERROR) ───────────── -->
    <div
      v-else-if="isResultState"
      class="draft-step-content result-state"
    >
       <!-- Async / Error Banners --> <b-alert
        v-if="jobExpired"
        show
        variant="warning"
        class="result-alert"
        > <i class="fas fa-clock mr-1"></i> Không tìm thấy job (có thể đã hết hạn). Vào danh sách
        nháp để kiểm tra kết quả. </b-alert
      > <b-alert
        v-if="jobTimeout"
        show
        variant="info"
        class="result-alert"
        > <i class="fas fa-hourglass-half mr-1"></i> Job đang xử lý lâu hơn bình thường. Vào danh
        sách nháp để xem kết quả sau. </b-alert
      > <b-alert
        v-if="modalState === 'ERROR'"
        show
        variant="danger"
        class="result-alert"
        > <i class="fas fa-exclamation-triangle mr-1"></i> {{
          resultErrorMessage || 'Đã xảy ra lỗi khi tạo nháp. Vui lòng thử lại.'
        }} </b-alert
      >
      <div
        class="result-overview"
        :class="`result-overview--${resultOverview.variant}`"
      >

        <div class="result-overview__icon"> <i :class="resultOverview.icon"></i> </div>

        <div class="result-overview__content">

          <div class="result-overview__eyebrow">{{ resultOverview.eyebrow }}</div>

          <h6 class="result-overview__title mb-1">{{ resultOverview.title }}</h6>

          <p class="result-overview__subtitle mb-0">{{ resultOverview.subtitle }}</p>

        </div>

      </div>
       <!-- Summary Cards -->
      <div class="result-summary-cards">

        <div
          class="result-card result-card--success"
          :class="{ 'is-active': activeTab === 'created' }"
          @click="activeTab = 'created'"
          role="button"
          tabindex="0"
          @keydown.enter="activeTab = 'created'"
          :aria-selected="activeTab === 'created'"
        >

          <div class="result-card-icon"> <i class="fas fa-check-circle"></i> </div>

          <div class="result-card-info">
             <span class="result-card-count">{{ resultCreated.length }}</span
            > <span class="result-card-label">Đã tạo</span>
          </div>

        </div>

        <div
          class="result-card result-card--info"
          :class="{ 'is-active': activeTab === 'existing' }"
          @click="activeTab = 'existing'"
          role="button"
          tabindex="0"
          @keydown.enter="activeTab = 'existing'"
          :aria-selected="activeTab === 'existing'"
        >

          <div class="result-card-icon"> <i class="fas fa-copy"></i> </div>

          <div class="result-card-info">
             <span class="result-card-count">{{ resultExisting.length }}</span
            > <span class="result-card-label">Đã có nháp</span>
          </div>

        </div>

        <div
          class="result-card result-card--warning"
          :class="{ 'is-active': activeTab === 'needs_info' }"
          @click="activeTab = 'needs_info'"
          role="button"
          tabindex="0"
          @keydown.enter="activeTab = 'needs_info'"
          :aria-selected="activeTab === 'needs_info'"
        >

          <div class="result-card-icon"> <i class="fas fa-exclamation-circle"></i> </div>

          <div class="result-card-info">
             <span class="result-card-count">{{ result.needsInfo.length }}</span
            > <span class="result-card-label">Cần bổ sung</span>
          </div>

        </div>

        <div
          class="result-card result-card--danger"
          :class="{ 'is-active': activeTab === 'failed' }"
          @click="activeTab = 'failed'"
          role="button"
          tabindex="0"
          @keydown.enter="activeTab = 'failed'"
          :aria-selected="activeTab === 'failed'"
        >

          <div class="result-card-icon"> <i class="fas fa-times-circle"></i> </div>

          <div class="result-card-info">
             <span class="result-card-count">{{ result.failed.length }}</span
            > <span class="result-card-label">Thất bại</span>
          </div>

        </div>

      </div>
       <!-- Tab Content -->
      <div class="result-tab-content mt-3">
         <!-- Created Tab -->
        <div v-show="activeTab === 'created'">

          <div
            v-if="resultCreated.length === 0"
            class="result-empty-state"
          >
             <i class="fas fa-inbox"></i> <span>Không có hóa đơn nào vừa được tạo mới</span>
          </div>

          <div
            v-else
            class="result-items-list"
          >

            <div
              v-for="item in resultCreated"
              :key="item.billId || item.draftId"
              class="result-item result-item--success"
            >

              <div class="result-item-main">

                <div class="result-item-code">
                   <i class="fas fa-check-circle text-success mr-1"></i> <strong>{{
                    getResultItemLabel(item)
                  }}</strong
                  > <b-badge
                    v-if="item.status === 'draft'"
                    variant="success"
                    class="ml-2"
                    style="font-size: 0.65rem"
                    >Đã tạo</b-badge
                  >
                </div>

                <div
                  v-if="item.buyerName"
                  class="result-item-meta"
                >
                   <i class="fas fa-user mr-1"></i>{{ item.buyerName }}
                </div>

                <div
                  v-else-if="item.billId"
                  class="result-item-meta"
                >
                   <i class="fas fa-info-circle mr-1"></i>Draft đã tạo nhưng chưa có bill number để đối
                  chiếu trực tiếp từ kết quả này.
                </div>

              </div>
              <div
                class="d-flex align-items-center"
                style="gap: 0.25rem"
              >
                 <b-button
                  v-if="canOpenSourceBill(item)"
                  size="sm"
                  variant="outline-secondary"
                  @click="openSourceBill(item.billNumber)"
                  > <i class="fas fa-file-alt mr-1"></i>Xem bill </b-button
                > <b-button
                  size="sm"
                  variant="outline-primary"
                  @click="openDraft(item)"
                  > <i class="fas fa-external-link-alt mr-1"></i>Mở nháp </b-button
                >
              </div>
            </div>

          </div>

        </div>
         <!-- Existing Tab -->
        <div v-show="activeTab === 'existing'">

          <div
            v-if="resultExisting.length === 0"
            class="result-empty-state"
          >
             <i class="fas fa-inbox"></i> <span>Không có hóa đơn nào đã có nháp từ trước</span>
          </div>

          <div
            v-else
            class="result-items-list"
          >

            <div
              v-for="item in resultExisting"
              :key="item.billId || item.draftId"
              class="result-item result-item--info"
            >

              <div class="result-item-main">

                <div class="result-item-code">
                   <i class="fas fa-copy text-info mr-1"></i> <strong>{{
                    getResultItemLabel(item)
                  }}</strong
                  > <b-badge
                    variant="info"
                    class="ml-2"
                    style="font-size: 0.65rem"
                    > Đã có nháp </b-badge
                  >
                </div>

                <div class="result-item-meta">
                   <i class="fas fa-info-circle mr-1"></i>
                  {{
                    item.billNumber
                      ? 'Hệ thống không tạo trùng, bạn nên tiếp tục trên draft hiện có.'
                      : 'Draft cũ đã tồn tại nhưng kết quả này không có bill number để mở bill detail.'
                  }}
                </div>

              </div>
              <div
                class="d-flex align-items-center"
                style="gap: 0.25rem"
              >
                 <b-button
                  v-if="canOpenSourceBill(item)"
                  size="sm"
                  variant="outline-secondary"
                  @click="openSourceBill(item.billNumber)"
                  > <i class="fas fa-file-alt mr-1"></i>Xem bill </b-button
                > <b-button
                  size="sm"
                  variant="outline-info"
                  @click="openDraft(item)"
                  > <i class="fas fa-external-link-alt mr-1"></i>Mở nháp hiện có </b-button
                >
              </div>
            </div>

          </div>

        </div>
         <!-- Needs Info Tab -->
        <div v-show="activeTab === 'needs_info'">

          <div
            v-if="result.needsInfo.length === 0"
            class="result-empty-state"
          >
             <i class="fas fa-inbox"></i> <span>Không có hóa đơn nào cần bổ sung</span>
          </div>

          <div
            v-else
            class="result-items-list"
          >

            <div
              v-for="item in result.needsInfo"
              :key="item.billId || item.draftId"
              class="result-item result-item--warning"
            >

              <div class="result-item-main">

                <div class="result-item-code">
                   <i class="fas fa-exclamation-circle text-warning mr-1"></i> <strong>{{
                    getResultItemLabel(item)
                  }}</strong
                  >
                </div>

                <div class="result-item-missing">
                   <span
                    v-for="(field, fIdx) in item.missingFields"
                    :key="fIdx"
                    class="missing-tag"
                    > {{ field }} </span
                  > <span
                    v-if="!item.missingFields.length"
                    class="text-muted"
                    > Xem nháp để biết chi tiết </span
                  >
                </div>

              </div>

              <div
                class="d-flex align-items-center"
                style="gap: 0.25rem"
              >
                 <b-button
                  v-if="isBulkBatch && bulkMode === 'PER_BILL'"
                  size="sm"
                  variant="outline-info"
                  @click="navigateToGridError(item)"
                  > <i class="fas fa-pencil-alt mr-1"></i>Sửa </b-button
                > <b-button
                  size="sm"
                  variant="outline-warning"
                  @click="openDraft(item)"
                  > <i class="fas fa-edit mr-1"></i>Mở sửa nháp </b-button
                >
              </div>

            </div>

          </div>

        </div>
         <!-- Failed Tab -->
        <div v-show="activeTab === 'failed'">

          <div
            v-if="result.failed.length === 0"
            class="result-empty-state"
          >
             <i class="fas fa-inbox"></i> <span>Không có hóa đơn nào thất bại</span>
          </div>

          <div
            v-else
            class="result-items-list"
          >

            <div
              v-for="item in result.failed"
              :key="item.billId || item.draftId || item.billCode"
              class="result-item result-item--danger"
            >

              <div class="result-item-main">

                <div class="result-item-code">
                   <i class="fas fa-times-circle text-danger mr-1"></i> <strong>{{
                    item.billCode || item.billId
                  }}</strong
                  > <b-badge
                    v-if="item.reasonCode"
                    variant="danger"
                    class="ml-2"
                    style="font-size: 0.65rem"
                    > {{ item.reasonCode }} </b-badge
                  >
                </div>

                <div class="result-item-reason">
                   <span class="reason-label">{{ getErrorLabel(item) }}</span
                  >
                </div>

              </div>

              <div
                class="d-flex align-items-center"
                style="gap: 0.25rem"
              >
                 <b-button
                  size="sm"
                  variant="outline-secondary"
                  :id="`detail-pop-${item.billId || item.draftId || item.billCode}`"
                  > <i class="fas fa-info-circle mr-1"></i>Chi tiết </b-button
                > <b-button
                  v-if="canOpenSourceBill(item)"
                  size="sm"
                  variant="outline-light"
                  @click="openSourceBill(item.billNumber)"
                  > <i class="fas fa-file-alt mr-1"></i>Gốc </b-button
                >
              </div>
               <b-popover
                :target="`detail-pop-${item.billId || item.draftId || item.billCode}`"
                triggers="click blur"
                placement="left"
                custom-class="draft-error-popover"
                > <template #title
                  > <i class="fas fa-exclamation-triangle text-danger mr-1"></i> {{
                    getErrorLabel(item)
                  }} </template
                >
                <p class="mb-2">{{ getErrorDescription(item) }}</p>

                <div
                  v-if="getErrorHowToFix(item)"
                  class="popover-fix-guide"
                >
                   <strong>Cách sửa:</strong> <span>{{ getErrorHowToFix(item) }}</span
                  >
                </div>
                 <small
                  v-if="item.billId"
                  class="text-muted d-block mt-2"
                  > Bill ID: <code>{{ item.billId }}</code
                  > </small
                > </b-popover
              >
            </div>

          </div>

        </div>

      </div>

    </div>
     <!-- ─── Footer ────────────────────────────────────────── --> <template #modal-footer
      > <!-- Step 1: Buyer Info Footer -->
      <div
        v-if="modalState === 'BUYER_INFO'"
        class="draft-footer w-100"
      >

        <div
          v-if="stepOneFooterHint"
          class="draft-footer-meta text-muted"
        >
           {{ stepOneFooterHint }}
        </div>
         <b-button
          variant="light"
          size="sm"
          @click="$refs.modal.hide()"
          > Huỷ </b-button
        > <b-button
          variant="primary"
          size="sm"
          :disabled="!canCreateDraft"
          @click="goToNextStep"
          > Tiếp tục <i class="fas fa-arrow-right ml-1"></i> </b-button
        >
      </div>
       <!-- Step 2: VAT Preview Footer -->
      <div
        v-else-if="modalState === 'VAT_PREVIEW'"
        class="draft-footer w-100"
      >

        <div
          v-if="previewGuardMessage"
          class="draft-footer-meta text-muted"
        >
           {{ previewGuardMessage }}
        </div>
         <b-button
          variant="light"
          size="sm"
          @click="goToPreviousStep"
          > <i class="fas fa-arrow-left mr-1"></i> Quay lại </b-button
        > <b-button
          variant="primary"
          size="sm"
          :disabled="!canConfirmPreview"
          @click="onClickCreate"
          > <i class="fas fa-file-medical mr-1"></i> Xác nhận nháp </b-button
        >
      </div>
       <!-- Loading Footer -->
      <div
        v-else-if="isLoadingState"
        class="draft-footer w-100"
      >
         <b-button
          variant="light"
          size="sm"
          @click="$refs.modal.hide()"
          > Huỷ </b-button
        > <span
          class="text-muted ml-auto"
          style="font-size: 0.8rem"
          > {{ loadingFooterText }} </span
        >
      </div>
       <!-- Processing Footer (hidden actions) -->
      <div
        v-else-if="isProcessingState"
        class="draft-footer w-100"
      >
         <span
          class="text-muted"
          style="font-size: 0.8rem"
          > Vui lòng chờ xử lý hoàn tất… </span
        >
      </div>
       <!-- Result Footer -->
      <div
        v-else-if="isResultState"
        class="draft-footer w-100"
      >

        <div
          class="d-flex align-items-center flex-wrap"
          style="gap: 0.5rem"
        >
           <b-button
            v-if="modalState === 'ERROR'"
            variant="outline-danger"
            size="sm"
            :disabled="isCreateActionLocked || !previewReady"
            @click="retryCreate"
            > <i class="fas fa-redo mr-1"></i> Thử tạo lại </b-button
          > <b-button
            v-else
            variant="light"
            size="sm"
            :disabled="!exportEnabled"
            @click="exportReport"
            > <i class="fas fa-download mr-1"></i> Xuất báo cáo </b-button
          >
        </div>

        <div
          class="d-flex align-items-center"
          style="gap: 0.5rem"
        >
           <b-button
            variant="light"
            size="sm"
            @click="$refs.modal.hide()"
            > Đóng </b-button
          > <b-button
            variant="primary"
            size="sm"
            @click="openDraftList"
            > <i class="fas fa-list mr-1"></i> Mở danh sách nháp </b-button
          >
        </div>

      </div>
       </template
    > </b-modal
  >
</template>

<script>
  import { mapGetters } from 'vuex'
  import {
    EINVOICE_CREATE_DRAFT_BATCH,
    EINVOICE_POLL_JOB,
    EINVOICE_LOOKUP_TAX_CODE,
    EINVOICE_CLEAR_PREVIEW,
    EINVOICE_FETCH_FACETS_BATCH,
  } from '@/core/services/store/modules/einvoice/einvoice.module'
  import { normalizeSaleType } from '@/core/services/store/modules/einvoice/einvoice.contract'
  import {
    bucketizeItems,
    buildDraftOverride,
    computePollingInterval,
    validateBuyerInfo,
    isValidTaxCodeFormat,
    normalizeBillIds,
    createRetailBuyerDrafts,
  } from '@/view/pages/einvoice/utils/createDraft.utils'
  import { canBootstrapDraftFromFacet } from '@/core/services/store/modules/einvoice/einvoice.adapter'
  import {
    getBillTypeDisplayName,
    resolveEInvoiceDisplayBillTypeContext,
  } from '@/view/pages/bills/einvoice-sale-type.utils'
  import {
    getDraftReasonMeta,
    EINVOICE_BUYER_TYPE,
    EINVOICE_PROVIDER_MODE,
    EINVOICE_SALE_TYPE,
  } from '@/utils/einvoice.enum'
  import { BILL_TYPE } from '@/utils/enum'
  import { getToastInstance } from '@/utils/toastHelper'
  import BulkInputModeToggle from './createDraft/BulkInputModeToggle.vue'
  import SharedBuyerForm from './createDraft/SharedBuyerForm.vue'
  import BulkEditorGrid from './createDraft/BulkEditorGrid.vue'
  import SelectionWarningBanner from './createDraft/SelectionWarningBanner.vue'
  import BatchSizeWarning from './createDraft/BatchSizeWarning.vue'
  import VatPreviewSection from './createDraft/VatPreviewSection.vue'
  import {
    detectDefaultMode,
    computeFieldStates,
    applyFieldOverrides,
    materializeSharedOverridesToRows,
    shouldWarnBatchSize,
    FIELD_ACTION,
    validateGrid,
    ROW_STATUS,
    BUYER_TYPE,
    createRetailBuyerDraftBucket,
    syncRetailRowDraftFromActiveFields,
    normalizeDraftRowForPayload,
  } from '@/view/pages/einvoice/utils/bulkBuyer.utils'

  const { makeToastFailure, makeToastWarning } = getToastInstance()

  const MAX_POLL_MS = 10 * 60 * 1000 // 10 minutes
  const PREVIEW_LIMIT = 8
  const SUPPORTED_PROVIDER_MODES = Object.freeze([
    EINVOICE_PROVIDER_MODE.FAST_POS,
    EINVOICE_PROVIDER_MODE.FAST_PORTAL,
  ])

  const STEPS = Object.freeze([
    { key: 'buyer_info', label: 'Thông tin người mua' },
    { key: 'vat_preview', label: 'Xem trước VAT' },
    { key: 'result', label: 'Kết quả' },
  ])

  const STEP_INDEX_MAP = Object.freeze({
    IDLE: 0,
    LOADING: 0,
    BUYER_INFO: 0,
    VAT_PREVIEW: 1,
    VALIDATING: 1,
    SUBMITTING: 1,
    RESULT_SYNC: 2,
    RESULT_ASYNC: 2,
    DONE: 2,
    ERROR: 2,
  })

  export default {
    name: 'EInvoiceCreateDraftModal',

    components: {
      BulkInputModeToggle,
      SharedBuyerForm,
      BulkEditorGrid,
      SelectionWarningBanner,
      BatchSizeWarning,
      VatPreviewSection,
    },

    props: {
      asyncThreshold: { type: Number, default: 10 },
      defaultProviderMode: { type: String, default: EINVOICE_PROVIDER_MODE.FAST_POS },
      defaultPrefer: { type: String, default: 'AUTO' },
    },

    data() {
      return {
        modalState: 'IDLE',
        operation: {
          billIds: [],
          providerMode: EINVOICE_PROVIDER_MODE.FAST_POS,
          laneSaleType: EINVOICE_SALE_TYPE.RETAIL,
          billType: null,
          billTypeBySalesInvoiceId: {},
          entryPoint: 'BILL_DETAIL',
          prefer: 'AUTO',
        },
        preflightWarnings: [],
        buyerType: EINVOICE_BUYER_TYPE.ENTERPRISE,
        buyer: {
          customerName: '',
          customerEmail: '',
          customerPhone: '',
          customerTaxCode: '',
          customerAddress: '',
          customerIdNumber: '',
          buyerName: '', // Representative name for enterprise
        },
        buyerFieldsTouched: {},
        buyerTouched: false,
        taxLookupLoading: false,
        taxLookupStatus: null, // null | 'success' | 'not_found' | 'error'
        jobId: null,
        jobPollTimer: null,
        jobPollStart: null,
        jobProgress: { current: 0, total: 0, status: null },
        jobExpired: false,
        jobTimeout: false,
        resultErrorMessage: '',
        result: { ready: [], needsInfo: [], failed: [] },
        activeTab: 'created',
        // Bulk buyer mode
        bulkMode: 'SHARED', // 'SHARED' | 'PER_BILL'
        detectedMode: null,
        fieldOverrides: {}, // { fieldKey: { action: FIELD_ACTION, value? } }
        gridRows: [], // Array of row objects for BulkEditorGrid
        affectedBills: [], // For SelectionWarningBanner
        previewBillCodeMap: {},
        batchWarnThreshold: 30,
        isFetchingBillInfo: false,
        billInfoFetchError: null,
        sharedFormValid: true, // Track validation state from SharedBuyerForm
        isCreateActionLocked: false,
        fieldOverridesByType: {
          [EINVOICE_BUYER_TYPE.ENTERPRISE]: {},
          [EINVOICE_BUYER_TYPE.INDIVIDUAL]: {},
        },
        buyerDraftsByType: createRetailBuyerDrafts(),
        buyerBootstrapSnapshotMode: 'none',
        sourceBillsById: {},
        bootstrapSessionToken: 0,
      }
    },

    computed: {
      ...mapGetters([
        'einvoiceIsBatchActionLoading',
        'einvoiceFacetBySalesInvoiceId',
        'billInfoIsLoading',
        'vatPreviewIsLoading',
        'vatPreviewData',
        'vatPreviewError',
      ]),

      steps() {
        return STEPS
      },

      currentStepIndex() {
        return STEP_INDEX_MAP[this.modalState] ?? 0
      },

      modalTitle() {
        const type =
          this.operation.laneSaleType === EINVOICE_SALE_TYPE.WHOLESALE ? 'Bán buôn' : 'Bán lẻ'
        if (this.modalState === 'LOADING') return 'Đang tải thông tin…'
        if (this.modalState === 'BUYER_INFO') return `Tạo nháp HĐĐT · ${type}`
        if (this.modalState === 'VAT_PREVIEW') return 'Xem trước tính VAT'
        if (this.isResultState) return 'Kết quả tạo nháp HĐĐT'
        if (this.isProcessingState) return 'Đang xử lý…'
        return 'Tạo nháp HĐĐT'
      },

      modalClosable() {
        return !['SUBMITTING', 'VALIDATING'].includes(this.modalState)
      },

      isBatch() {
        return this.operation.billIds.length > 1
      },

      isProcessingState() {
        return ['LOADING', 'VALIDATING', 'SUBMITTING'].includes(this.modalState)
      },

      isLoadingState() {
        return ['LOADING'].includes(this.modalState)
      },

      isResultState() {
        return ['RESULT_SYNC', 'RESULT_ASYNC', 'DONE', 'ERROR'].includes(this.modalState)
      },

      exportEnabled() {
        return ['RESULT_SYNC', 'DONE'].includes(this.modalState) && this.totalResultCount > 0
      },

      totalResultCount() {
        return this.result.ready.length + this.result.needsInfo.length + this.result.failed.length
      },

      resultCreated() {
        return this.result.ready.filter(
          (item) => !item.isDuplicate && item.status !== 'already_created',
        )
      },

      resultExisting() {
        return this.result.ready.filter(
          (item) => item.isDuplicate || item.status === 'already_created',
        )
      },

      resultOverview() {
        if (this.modalState === 'ERROR') {
          return {
            eyebrow: 'Lỗi tạo draft',
            title: 'Flow tạo hóa đơn nháp GTGT chưa hoàn tất',
            subtitle:
              this.resultErrorMessage ||
              'Hệ thống chưa tạo được draft. Kiểm tra lỗi rồi thử lại để tránh tạo lặp hoặc bỏ sót bill.',
            variant: 'danger',
            icon: 'fas fa-exclamation-triangle',
          }
        }

        if (this.jobExpired || this.jobTimeout) {
          return {
            eyebrow: 'Đang chờ kết quả nền',
            title: 'Hệ thống chưa trả kết quả cuối cùng về modal này',
            subtitle:
              'Job có thể vẫn đang xử lý nền. Mở danh sách draft hoặc chi tiết HĐĐT để kiểm tra trạng thái mới nhất.',
            variant: 'info',
            icon: 'fas fa-hourglass-half',
          }
        }

        const createdCount = this.resultCreated.length
        const existingCount = this.resultExisting.length
        const needsInfoCount = this.result.needsInfo.length
        const failedCount = this.result.failed.length

        if (createdCount > 0 && !existingCount && !needsInfoCount && !failedCount) {
          return {
            eyebrow: 'Tạo draft thành công',
            title:
              createdCount === 1
                ? 'Đã tạo mới 1 draft GTGT'
                : `Đã tạo mới ${createdCount} draft GTGT`,
            subtitle: 'Bạn có thể mở draft ngay để rà soát VAT và tiếp tục phát hành.',
            variant: 'success',
            icon: 'fas fa-check-circle',
          }
        }

        if (!createdCount && existingCount > 0 && !needsInfoCount && !failedCount) {
          return {
            eyebrow: 'Draft đã tồn tại',
            title:
              existingCount === 1
                ? 'Bill này đã có draft GTGT từ trước'
                : `${existingCount} bill đã có draft GTGT từ trước`,
            subtitle: 'Hệ thống không tạo trùng. Mở draft hiện có để tiếp tục thay vì tạo lại.',
            variant: 'info',
            icon: 'fas fa-copy',
          }
        }

        if (failedCount > 0) {
          return {
            eyebrow: 'Có bill tạo draft thất bại',
            title: `Có ${failedCount} bill cần xử lý lỗi trước khi tiếp tục`,
            subtitle:
              'Ưu tiên đọc lỗi cụ thể, tránh submit lại mù vì sẽ chỉ lặp thêm thao tác sai.',
            variant: 'danger',
            icon: 'fas fa-times-circle',
          }
        }

        if (needsInfoCount > 0) {
          return {
            eyebrow: 'Thiếu dữ liệu để hoàn tất draft',
            title: `Có ${needsInfoCount} bill cần bổ sung thông tin người mua`,
            subtitle:
              'Flow đã chặn đúng chỗ. Sửa các trường còn thiếu rồi mới tiếp tục để tránh draft lỗi.',
            variant: 'warning',
            icon: 'fas fa-exclamation-circle',
          }
        }

        return {
          eyebrow: 'Kết quả tạo draft',
          title: 'Đã xử lý yêu cầu tạo hóa đơn nháp GTGT',
          subtitle: 'Kiểm tra từng nhóm kết quả bên dưới để quyết định bước tiếp theo.',
          variant: 'info',
          icon: 'fas fa-info-circle',
        }
      },

      previewLimit() {
        return PREVIEW_LIMIT
      },

      previewBills() {
        return this.operation.billIds.slice(0, PREVIEW_LIMIT).map((billId) => {
          const key = String(billId)
          const sourceBill = this.sourceBillsById[key] || {}
          const displayCode = this.previewBillCodeMap[key] || sourceBill.billNumber || billId
          const billType = this.operation.billTypeBySalesInvoiceId[key] || sourceBill.billType || sourceBill.type
          return {
            billId,
            displayCode,
            detailRoute: this.buildSourceBillDetailRoute({
              billNumber: sourceBill.billNumber,
              billType,
            }),
          }
        })
      },

      previewInvoices() {
        return this.buildDraftRows()
          .map((row) => {
            const salesInvoiceId = row?.billId || row?.salesInvoiceId
            if (!salesInvoiceId) {
              return null
            }

            return {
              salesInvoiceId,
              override: buildDraftOverride(row),
            }
          })
          .filter(Boolean)
      },

      selectedBillTypes() {
        const normalized = this.operation.billIds.reduce((acc, billId) => {
          const billType = this.operation.billTypeBySalesInvoiceId[String(billId)]
          if (billType === null || billType === undefined || billType === '') {
            return acc
          }

          if (!acc.includes(billType)) {
            acc.push(billType)
          }

          return acc
        }, [])

        return normalized
      },

      saleTypeLabel() {
        const labels = this.selectedBillTypes.map((billType) => getBillTypeDisplayName(billType))
        const normalizedLabels = labels.filter(Boolean)
        if (normalizedLabels.length === 1) {
          return normalizedLabels[0]
        }
        if (normalizedLabels.length > 1) {
          return normalizedLabels.join(' + ')
        }

        return (
          getBillTypeDisplayName(this.operation.billType) ||
          (this.operation.laneSaleType === EINVOICE_SALE_TYPE.WHOLESALE ? 'Bán buôn' : 'Bán lẻ')
        )
      },

      saleTypeIcon() {
        return this.operation.laneSaleType === EINVOICE_SALE_TYPE.WHOLESALE
          ? 'fas fa-building text-info'
          : 'fas fa-cash-register text-success'
      },

      progressPercent() {
        if (!this.jobProgress.total) return 0
        return Math.round((this.jobProgress.current / this.jobProgress.total) * 100)
      },

      buyerValidation() {
        return validateBuyerInfo(this.buyerType, this.buyer, {
          isWholesale: this.operation.laneSaleType === EINVOICE_SALE_TYPE.WHOLESALE,
        })
      },

      buyerErrors() {
        return this.buyerValidation.errors
      },

      hasBuyerErrors() {
        return !this.buyerValidation.valid
      },

      canLookupTax() {
        return isValidTaxCodeFormat(this.buyer.customerTaxCode)
      },

      canCreateDraft() {
        // Single bill or SHARED mode: use SharedBuyerForm validation
        if (!this.isBulkBatch || this.bulkMode === 'SHARED') {
          return this.sharedFormValid
        }
        return !this.hasBulkGridIssues
      },

      bulkGridValidation() {
        return validateGrid(this.gridRows)
      },

      hasBulkGridIssues() {
        return (
          this.bulkGridValidation.summary.missing > 0 || this.bulkGridValidation.summary.invalid > 0
        )
      },

      previewReady() {
        return !this.vatPreviewIsLoading && !this.vatPreviewError && Boolean(this.vatPreviewData)
      },

      previewErrorBills() {
        const bills = Array.isArray(this.vatPreviewData?.bills) ? this.vatPreviewData.bills : []
        return bills.filter((bill) => (bill?.status || '').toString().trim().toLowerCase() === 'error')
      },

      previewHasBillErrors() {
        return this.previewErrorBills.length > 0
      },

      previewErrorBillCount() {
        return this.previewErrorBills.length
      },

      canConfirmPreview() {
        return (
          this.previewReady &&
          !this.previewHasBillErrors &&
          !this.einvoiceIsBatchActionLoading &&
          !this.isCreateActionLocked
        )
      },

      previewGuardMessage() {
        if (this.einvoiceIsBatchActionLoading) {
          return 'Hệ thống đang tạo nháp…'
        }
        if (this.vatPreviewIsLoading) {
          return 'Đang tải xem trước VAT…'
        }
        if (this.vatPreviewError) {
          return 'Cần tải xem trước VAT thành công trước khi xác nhận nháp.'
        }
        if (!this.vatPreviewData) {
          return 'Chưa có dữ liệu xem trước VAT.'
        }
        if (this.previewHasBillErrors) {
          return `Có ${this.previewErrorBillCount} hóa đơn preview lỗi. Cần xử lý trước khi xác nhận nháp.`
        }
        return ''
      },

      isBulkBatch() {
        return this.operation.billIds.length >= 2
      },

      stepOneFooterHint() {
        if (!this.isBulkBatch || this.bulkMode === 'SHARED') {
          return this.sharedFormValid
            ? ''
            : 'Hoàn tất các trường bắt buộc trước khi sang bước xem trước VAT.'
        }

        const { missing, invalid } = this.bulkGridValidation.summary
        if (!missing && !invalid) {
          return ''
        }

        const fragments = []
        if (missing) fragments.push(`${missing} hóa đơn thiếu thông tin`)
        if (invalid) fragments.push(`${invalid} hóa đơn sai định dạng`)
        return `Cần xử lý ${fragments.join(' · ')} trước khi tiếp tục.`
      },

      /**
       * Compute field states for SharedBuyerForm.
       * For single bill: creates uniform field states from buyer data
       * For batch: computes from grid rows (may be mixed)
       */
      computedFieldStates() {
        const fieldMap = this.getFieldsForBuyerType()

        // Single bill: all fields are "uniform" with current buyer values
        if (!this.isBulkBatch) {
          // Map SharedBuyerForm field names to modal's buyer field names
          const sharedToBuyerMapping = {
            buyerName: 'customerName',
            buyerEmail: 'customerEmail',
            buyerPhone: 'customerPhone',
            buyerTaxCode: 'customerTaxCode',
            buyerAddress: 'customerAddress',
            buyerIdNumber: 'customerIdNumber',
            buyerContactPerson: 'buyerName', // Representative name
          }

          const states = {}
          fieldMap.forEach((fieldKey) => {
            const buyerKey = sharedToBuyerMapping[fieldKey] || fieldKey
            const value = this.buyer[buyerKey] || ''
            states[fieldKey] = {
              isMixed: false,
              uniformValue: value,
              distinctValues: [value],
            }
          })
          return states
        }

        // Batch mode: compute from grid rows
        return computeFieldStates(this.gridRows, fieldMap)
      },

      hasUnsavedBulkData() {
        if (this.bulkMode === 'SHARED') {
          return Object.values(this.fieldOverrides).some((o) => o.action !== FIELD_ACTION.KEEP)
        }
        // For PER_BILL mode, check if any grid cell was edited
        return this.gridRows.some((row) => row._dirty)
      },

      showBatchSizeWarning() {
        return shouldWarnBatchSize(this.operation.billIds.length, this.batchWarnThreshold)
      },

      modalSize() {
        return 'xl'
        // if (this.isBulkBatch && this.bulkMode === 'PER_BILL' && this.modalState === 'BUYER_INFO') {
        //   return 'xl'
        // }
        // return 'lg'
      },

      // Check if we're on buyer info step
      isBuyerInfoStep() {
        return this.modalState === 'BUYER_INFO'
      },

      // Check if we're on VAT preview step
      isVatPreviewStep() {
        return this.modalState === 'VAT_PREVIEW'
      },

      loadingStateTitle() {
        return 'Đang tải thông tin hóa đơn…'
      },

      loadingStateSubtitle() {
        return 'Hệ thống đang lấy thông tin khách hàng từ các hóa đơn đã chọn.'
      },

      loadingFooterText() {
        return 'Đang tải thông tin…'
      },
    },

    watch: {
      bulkMode(nextMode, previousMode) {
        if (!this.isBulkBatch || nextMode === previousMode) {
          return
        }

        if (previousMode === 'SHARED' && nextMode === 'PER_BILL') {
          this.materializeSharedOverridesIntoRows()
        }

        if (previousMode === 'PER_BILL' && nextMode === 'SHARED') {
          this.initEmptyFieldOverridesForSharedMode()
        }
      },
    },

    beforeDestroy() {
      this.stopPolling()
    },

    methods: {
      normalizeProviderMode(providerMode) {
        if (typeof providerMode !== 'string') {
          return null
        }

        const normalized = providerMode.trim().toUpperCase()
        return SUPPORTED_PROVIDER_MODES.includes(normalized) ? normalized : null
      },

      resolveOperationEntryContract({ billIds, providerMode, billTypeBySalesInvoiceId }) {
        const normalizedProviderMode = this.normalizeProviderMode(providerMode)
        if (!normalizedProviderMode) {
          const error = new Error('Thiếu providerMode hợp lệ để mở flow tạo draft HĐĐT')
          error.code = 'EINVOICE_PROVIDER_MODE_REQUIRED'
          throw error
        }

        const laneSaleType = normalizeSaleType(normalizedProviderMode)
        if (!laneSaleType) {
          const error = new Error('Không xác định được lane e-invoice từ providerMode hiện tại')
          error.code = 'EINVOICE_SALE_TYPE_REQUIRED'
          throw error
        }

        if (
          !billTypeBySalesInvoiceId ||
          typeof billTypeBySalesInvoiceId !== 'object' ||
          Array.isArray(billTypeBySalesInvoiceId)
        ) {
          const error = new Error('Thiếu billTypeBySalesInvoiceId tại cổng vào modal tạo draft')
          error.code = 'EINVOICE_BILL_TYPE_MAP_REQUIRED'
          throw error
        }

        const normalizedBillTypeBySalesInvoiceId = {}
        const uniqueBillTypes = []

        billIds.forEach((billId) => {
          const key = String(billId)
          if (!Object.prototype.hasOwnProperty.call(billTypeBySalesInvoiceId, key)) {
            const error = new Error(`Thiếu billType cho hóa đơn ${key}`)
            error.code = 'EINVOICE_BILL_TYPE_REQUIRED'
            throw error
          }

          const billType = billTypeBySalesInvoiceId[key]
          const entryContext = resolveEInvoiceDisplayBillTypeContext({
            billType,
            saleType: laneSaleType,
            providerMode: normalizedProviderMode,
          })
          const normalizedBillType = Number(entryContext.billType)
          const resolvedBillType = Number.isInteger(normalizedBillType)
            ? normalizedBillType
            : entryContext.billType

          normalizedBillTypeBySalesInvoiceId[key] = resolvedBillType
          if (!uniqueBillTypes.includes(resolvedBillType)) {
            uniqueBillTypes.push(resolvedBillType)
          }
        })

        return {
          providerMode: normalizedProviderMode,
          laneSaleType,
          billTypeBySalesInvoiceId: normalizedBillTypeBySalesInvoiceId,
          uniqueBillTypes,
        }
      },

      // ── Public API ─────────────────────────────────────────────
      async open({
        billIds,
        providerMode,
        entryPoint = 'BILL_DETAIL',
        prefer = 'AUTO',
        buyerType,
        buyer,
        bills,
        billTypeBySalesInvoiceId,
      } = {}) {
        this.reset()
        const normalizedBillIds = normalizeBillIds(billIds)
        if (!normalizedBillIds.length) {
          makeToastFailure('Vui lòng chọn ít nhất 1 hóa đơn')
          return
        }

        let entryContract = null
        try {
          entryContract = this.resolveOperationEntryContract({
            billIds: normalizedBillIds,
            providerMode,
            billTypeBySalesInvoiceId,
          })
        } catch (error) {
          makeToastFailure(error?.message || 'Contract mở modal tạo draft không hợp lệ')
          return
        }

        this.operation = {
          billIds: normalizedBillIds,
          providerMode: entryContract.providerMode,
          laneSaleType: entryContract.laneSaleType,
          billType:
            entryContract.uniqueBillTypes.length === 1 ? entryContract.uniqueBillTypes[0] : null,
          billTypeBySalesInvoiceId: entryContract.billTypeBySalesInvoiceId,
          entryPoint,
          prefer: prefer || this.defaultPrefer,
        }
        this.sourceBillsById = this.buildSourceBillsById(bills)
        this.previewBillCodeMap = this.buildPreviewBillCodeMap(bills)
        // Pre-fill buyer info if provided by caller
        if (buyerType) {
          this.buyerType = buyerType
        }
        if (buyer) {
          Object.assign(this.buyer, buyer)
        }

        this.$refs.modal.show()
        this.$emit('opened', {
          billIds: this.operation.billIds,
          saleType: this.operation.laneSaleType,
          providerMode: this.operation.providerMode,
        })

        const sessionToken = ++this.bootstrapSessionToken
        await this.fetchAndInitFacetBuyerBootstrap({ sessionToken })
      },

      buildPreviewBillCodeMap(bills = []) {
        if (!Array.isArray(bills)) {
          return {}
        }

        return bills.reduce((acc, bill) => {
          const billId = bill?.id || bill?.billId || bill?.saleInvoiceId
          if (!billId) {
            return acc
          }

          const displayCode =
            bill?.billNumber || bill?.billCode || bill?.saleInvoiceCode || bill?.code

          if (displayCode) {
            acc[String(billId)] = displayCode
          }

          return acc
        }, {})
      },

      buildSourceBillsById(bills = []) {
        if (!Array.isArray(bills)) {
          return {}
        }

        return bills.reduce((acc, bill) => {
          const billId = bill?.id || bill?.billId || bill?.saleInvoiceId
          if (billId) {
            acc[String(billId)] = bill
          }
          return acc
        }, {})
      },

      getSourceBill(billId) {
        return this.sourceBillsById[String(billId)] || {}
      },

      buildSourceBillDetailRoute({ billNumber, billType } = {}) {
        const normalizedBillNumber =
          typeof billNumber === 'string' ? billNumber.trim() : billNumber

        if (!normalizedBillNumber) {
          return null
        }

        const normalizedBillType = Number(billType)
        const routeNameByBillType = {
          [BILL_TYPE.RETAIL_INVOICE]: 'update-bill',
          [BILL_TYPE.SHIPPING]: 'update-delivery-bill',
          [BILL_TYPE.WHOLESALE_INVOICE]: 'update-wholesale-invoice',
        }
        const routeName = routeNameByBillType[normalizedBillType]

        if (!routeName) {
          return null
        }

        return {
          name: routeName,
          query: { id: normalizedBillNumber },
        }
      },

      getFacetBootstrapEntry(salesInvoiceId) {
        const facet = this.einvoiceFacetBySalesInvoiceId(
          salesInvoiceId,
          this.operation.laneSaleType,
        )
        if (!canBootstrapDraftFromFacet(facet)) {
          return {
            snapshotMode: 'none',
            buyerCompany: {},
            buyerIndividual: {},
          }
        }

        return {
          snapshotMode: facet.snapshotMode || 'none',
          buyerCompany: facet.buyerCompany || {},
          buyerIndividual: facet.buyerIndividual || {},
        }
      },

      redirectToExistingDraft(salesInvoiceId) {
        if (!salesInvoiceId) {
          return
        }

        this.$router.push({
          name: 'einvoice-detail',
          params: { salesInvoiceId },
          query: { saleType: this.operation.laneSaleType },
        })
      },

      createFacetGridRowFromFacet(salesInvoiceId) {
        const sourceBill = this.getSourceBill(salesInvoiceId)
        const facetEntry = this.getFacetBootstrapEntry(salesInvoiceId)
        const buyerCompany = createRetailBuyerDraftBucket(facetEntry.buyerCompany)
        const buyerIndividual = createRetailBuyerDraftBucket(facetEntry.buyerIndividual)
        const buyerType = BUYER_TYPE.ENTERPRISE

        return {
          ...syncRetailRowDraftFromActiveFields({
            billId: salesInvoiceId,
            billCode:
              sourceBill.billNumber ||
              sourceBill.billCode ||
              this.previewBillCodeMap[String(salesInvoiceId)] ||
              salesInvoiceId,
            customerId: sourceBill.customerId || null,
            customerName: buyerCompany.buyerName,
            customerEmail: buyerCompany.buyerEmail,
            customerPhone: buyerCompany.buyerPhone,
            customerTaxCode: buyerCompany.buyerTaxCode,
            customerAddress: buyerCompany.buyerAddress,
            customerIdNumber: buyerCompany.buyerIdNumber,
            contactPersonName: '',
            snapshotMode: facetEntry.snapshotMode,
            buyerType,
            facetBuyerCompany: buyerCompany,
            facetBuyerIndividual: buyerIndividual,
            buyerCompanyDraft: buyerCompany,
            buyerIndividualDraft: buyerIndividual,
            buyerName: buyerCompany.buyerName,
            buyerEmail: buyerCompany.buyerEmail,
            buyerPhone: buyerCompany.buyerPhone,
            buyerTaxCode: buyerCompany.buyerTaxCode,
            buyerAddress: buyerCompany.buyerAddress,
            buyerIdNumber: '',
            _dirty: false,
            validation: null,
          }),
          _dirty: false,
        }
      },

      hydrateSingleBuyerFromFacet(salesInvoiceId) {
        const facetEntry = this.getFacetBootstrapEntry(salesInvoiceId)
        this.buyerBootstrapSnapshotMode = facetEntry.snapshotMode
        this.buyerDraftsByType = createRetailBuyerDrafts(
          facetEntry.snapshotMode === 'none'
            ? {}
            : {
                buyerCompany: facetEntry.buyerCompany,
                buyerIndividual: facetEntry.buyerIndividual,
              },
        )
        this.buyer = {
          ...this.buyerDraftsByType[this.buyerType],
        }
        this.fieldOverrides = { ...(this.fieldOverridesByType[this.buyerType] || {}) }
      },

      /**
       * Initialize grid rows from bills data array.
       * @param {Array} bills - Array of bill objects with customer info
       */
      initGridFromBills(bills) {
        const billsData = bills.map((b) => ({
          billId: b.id || b.billId,
          billCode: b.billNumber || b.billCode || b.billId,
          customerId: b.customerId || null,
          buyerType: this.detectBuyerTypeFromBill(b),
          customerName: b.customerName || '',
          customerEmail: b.customerEmail || '',
          customerPhone: b.customerPhone || b.phone || '',
          customerTaxCode: b.customerTaxCode || '',
          customerAddress: b.customerAddress || '',
          customerIdNumber: b.customerIdNumber || '',
          buyerName: b.customerName || b.buyerName || '',
          buyerEmail: b.customerEmail || '',
          buyerPhone: b.customerPhone || b.phone || '',
          buyerTaxCode: b.customerTaxCode || '',
          buyerAddress: b.customerAddress || '',
          buyerIdNumber: b.customerIdNumber || '',
        }))

        this.gridRows = billsData
        this.previewBillCodeMap = {
          ...this.previewBillCodeMap,
          ...billsData.reduce((acc, row) => {
            if (row.billId && row.billCode) {
              acc[String(row.billId)] = row.billCode
            }
            return acc
          }, {}),
        }
        this.detectedMode = detectDefaultMode(billsData)
        this.bulkMode = this.detectedMode
        this.fieldOverrides = {}
      },

      /**
       * Detect buyer type from bill data based on available fields.
       * @param {Object} bill - Bill object
       * @returns {'ENTERPRISE'|'INDIVIDUAL'}
       */
      detectBuyerTypeFromBill(bill) {
        // ENTERPRISE: Has valid tax code format
        if (bill.customerTaxCode && isValidTaxCodeFormat(bill.customerTaxCode)) {
          return 'ENTERPRISE'
        }
        // Default the draft grid to enterprise-first so users fill legal invoice data first
        return 'ENTERPRISE'
      },

      async fetchAndInitFacetBuyerBootstrap({ sessionToken }) {
        this.modalState = 'LOADING'
        this.isFetchingBillInfo = true
        this.billInfoFetchError = null

        try {
          const missingIds = this.operation.billIds.filter(
            (billId) =>
              !this.einvoiceFacetBySalesInvoiceId(billId, this.operation.laneSaleType),
          )

          if (missingIds.length) {
            await this.$store.dispatch(EINVOICE_FETCH_FACETS_BATCH, {
              salesInvoiceIds: missingIds,
              saleType: this.operation.laneSaleType,
            })
          }

          if (sessionToken !== this.bootstrapSessionToken) {
            return
          }

          const blockedBillId = this.operation.billIds.find((billId) => {
            const facet = this.einvoiceFacetBySalesInvoiceId(
              billId,
              this.operation.laneSaleType,
            )
            return !canBootstrapDraftFromFacet(facet)
          })

          if (blockedBillId) {
            makeToastWarning(
              'Hóa đơn không đủ điều kiện bootstrap draft. Hệ thống sẽ mở draft hiện có để bạn tiếp tục.',
            )
            this.$emit('bootstrap-blocked', {
              billIds: this.operation.billIds,
              blockedBillId,
              saleType: this.operation.laneSaleType,
              providerMode: this.operation.providerMode,
            })
            this.redirectToExistingDraft(blockedBillId)
            this.$refs.modal.hide()
            return
          }

          if (this.isBulkBatch) {
            this.gridRows = this.operation.billIds.map((billId) =>
              this.createFacetGridRowFromFacet(billId),
            )
            this.detectedMode = detectDefaultMode(this.gridRows)
            this.bulkMode = this.detectedMode

            // Init empty form for SHARED mode
            if (this.bulkMode === 'SHARED') {
              this.initEmptyFieldOverridesForSharedMode()
            }
          } else {
            this.hydrateSingleBuyerFromFacet(this.operation.billIds[0])
          }

          this.modalState = 'BUYER_INFO'
        } catch (err) {
          if (sessionToken !== this.bootstrapSessionToken) {
            return
          }

          const message =
            err?.message ||
            err?.response?.data?.message ||
            'Không thể bootstrap draft vì facet canonical không sẵn sàng.'
          this.billInfoFetchError = message
          makeToastFailure(message)
          this.$emit('bootstrap-blocked', {
            billIds: this.operation.billIds,
            blockedBillId: null,
            saleType: this.operation.laneSaleType,
            providerMode: this.operation.providerMode,
            reason: message,
          })
          this.$refs.modal.hide()
        } finally {
          if (sessionToken === this.bootstrapSessionToken) {
            this.isFetchingBillInfo = false
          }
        }
      },

      hide() {
        this.$refs.modal.hide()
      },

      // ── Buyer info ─────────────────────────────────────────────
      setBuyerType(type) {
        if (type === this.buyerType) {
          return
        }

        if (!this.isBulkBatch) {
          this.buyerDraftsByType = {
            ...this.buyerDraftsByType,
            [this.buyerType]: {
              ...this.buyer,
            },
          }
        } else if (this.bulkMode === 'SHARED') {
          this.fieldOverridesByType = {
            ...this.fieldOverridesByType,
            [this.buyerType]: {
              ...this.fieldOverrides,
            },
          }
        }

        this.buyerType = type

        if (!this.isBulkBatch) {
          this.buyer = {
            ...this.buyerDraftsByType[type],
          }
          this.buyerFieldsTouched = {}
          this.buyerTouched = false
          this.taxLookupStatus = null
        }

        if (this.isBulkBatch && this.bulkMode === 'SHARED') {
          this.fieldOverrides = { ...(this.fieldOverridesByType[type] || {}) }
          return
        }

        this.fieldOverrides = { ...(this.fieldOverridesByType[type] || {}) }
      },

      /**
       * Handle overrides update from SharedBuyerForm.
       * For single bill: sync overrides back to buyer object
       * For batch: just store overrides as-is
       */
      onOverridesUpdate(newOverrides) {
        this.fieldOverrides = newOverrides
        this.fieldOverridesByType = {
          ...this.fieldOverridesByType,
          [this.buyerType]: { ...newOverrides },
        }

        // For single bill mode, sync the overrides back to buyer object
        if (!this.isBulkBatch) {
          this.syncOverridesToBuyer(newOverrides)
          this.buyerDraftsByType = {
            ...this.buyerDraftsByType,
            [this.buyerType]: {
              ...this.buyer,
            },
          }
        }
      },

      /**
       * Handle validation event from SharedBuyerForm.
       */
      onSharedFormValidation({ valid }) {
        this.sharedFormValid = valid
      },

      /**
       * Sync field overrides back to buyer object (for single bill mode).
       * Maps SharedBuyerForm field keys (buyerName) to modal's buyer keys (customerName).
       */
      syncOverridesToBuyer(overrides) {
        const fieldMapping = {
          buyerName: 'customerName',
          buyerEmail: 'customerEmail',
          buyerPhone: 'customerPhone',
          buyerTaxCode: 'customerTaxCode',
          buyerAddress: 'customerAddress',
          buyerIdNumber: 'customerIdNumber',
          buyerContactPerson: 'buyerName', // Representative name maps to buyerName
        }

        Object.entries(overrides).forEach(([fieldKey, override]) => {
          if (override.action === FIELD_ACTION.OVERRIDE && fieldMapping[fieldKey]) {
            this.$set(this.buyer, fieldMapping[fieldKey], override.value)
          }
        })
      },

      /**
       * Get relevant fields for current buyer type.
       * Uses SharedBuyerForm field naming (buyerName, buyerTaxCode, etc.)
       */
      getFieldsForBuyerType() {
        if (this.buyerType === EINVOICE_BUYER_TYPE.ENTERPRISE) {
          return [
            'buyerTaxCode',
            'buyerName',
            'buyerAddress',
            'buyerEmail',
            'buyerContactPerson',
            'buyerPhone',
          ]
        }
        // INDIVIDUAL
        return [
          'buyerName',
          'buyerIdNumber',
          'buyerTaxCode',
          'buyerAddress',
          'buyerEmail',
          'buyerPhone',
        ]
      },

      initEmptyFieldOverridesForSharedMode() {
        const allFields = [
          'buyerTaxCode',
          'buyerName',
          'buyerAddress',
          'buyerEmail',
          'buyerContactPerson',
          'buyerPhone',
          'buyerIdNumber',
        ]
        const emptyOverrides = {}
        allFields.forEach((fieldKey) => {
          emptyOverrides[fieldKey] = {
            action: FIELD_ACTION.OVERRIDE,
            value: '',
          }
        })
        this.fieldOverrides = emptyOverrides
        this.fieldOverridesByType = {
          [EINVOICE_BUYER_TYPE.ENTERPRISE]: { ...emptyOverrides },
          [EINVOICE_BUYER_TYPE.INDIVIDUAL]: { ...emptyOverrides },
        }
      },

      materializeSharedOverridesIntoRows() {
        this.gridRows = materializeSharedOverridesToRows(this.gridRows, {
          buyerType: this.buyerType,
          overrides: this.fieldOverrides,
          preserveDirty: true,
        })
        this.fieldOverridesByType = {
          ...this.fieldOverridesByType,
          [this.buyerType]: { ...this.fieldOverrides },
        }
        this.fieldOverrides = {}
      },

      resetBuyerFields() {
        this.buyer = {
          customerName: '',
          customerEmail: '',
          customerPhone: '',
          customerTaxCode: '',
          customerAddress: '',
          customerIdNumber: '',
          buyerName: '',
        }
        this.buyerFieldsTouched = {}
        this.buyerTouched = false
        this.taxLookupStatus = null
      },

      touchBuyerField(field) {
        this.$set(this.buyerFieldsTouched, field, true)
      },

      getBuyerFieldState(field) {
        if (!this.buyerFieldsTouched[field]) return null
        return this.buyerErrors[field] ? false : true
      },

      async lookupTaxCode() {
        if (!this.canLookupTax || this.taxLookupLoading) return
        this.taxLookupLoading = true
        this.taxLookupStatus = null
        try {
          const result = await this.$store.dispatch(
            EINVOICE_LOOKUP_TAX_CODE,
            this.buyer.customerTaxCode.trim(),
          )

          if (result && result.found) {
            // Auto-fill company info
            this.buyer.customerName = result.companyName || ''
            this.buyer.customerAddress = result.address || ''
            // Optional: also set email/phone if available
            if (result.email && !this.buyer.customerEmail) {
              this.buyer.customerEmail = result.email
            }
            this.taxLookupStatus = 'success'
          } else {
            this.taxLookupStatus = 'not_found'
          }
        } catch (err) {
          console.error('Tax code lookup failed:', err)
          this.taxLookupStatus = 'error'
        } finally {
          this.taxLookupLoading = false
        }
      },

      // ── Step Navigation ──────────────────────────────────────────
      async goToNextStep() {
        if (this.modalState === 'BUYER_INFO') {
          // Validate buyer info before proceeding (single bill or SHARED mode)
          if (!this.isBulkBatch || this.bulkMode === 'SHARED') {
            // Trigger validation in SharedBuyerForm
            if (this.$refs.sharedBuyerForm) {
              const result = this.$refs.sharedBuyerForm.validate()
              if (!result.valid) {
                return // Don't proceed if validation fails
              }
            }
            if (this.isBulkBatch && this.bulkMode === 'SHARED') {
              const sharedValidation = this.validateSharedRowsBeforeSubmit()
              if (!sharedValidation.valid) {
                this.notifySharedRowsValidationFailure(sharedValidation)
                return
              }
            }
          } else {
            const bulkValidation = this.validateBulkGrid()
            if (!bulkValidation.valid) {
              this.focusBulkGridError(bulkValidation.firstInvalidRow)
              return
            }
          }

          this.modalState = 'VAT_PREVIEW'
        }
      },

      goToPreviousStep() {
        if (this.modalState === 'VAT_PREVIEW') {
          this.modalState = 'BUYER_INFO'
        }
      },

      // ── State machine ──────────────────────────────────────────
      async onClickCreate() {
        if (!this.canConfirmPreview || this.isCreateActionLocked) {
          return
        }

        // Final validation before submitting
        if (!this.isBulkBatch) {
          this.buyerTouched = true
          let fieldsToTouch = ['customerName']
          if (this.buyerType === 'ENTERPRISE') {
            fieldsToTouch = [
              'customerName',
              'customerTaxCode',
              'customerAddress',
              'customerEmail',
            ]
          }
          // INDIVIDUAL only requires customerName
          fieldsToTouch.forEach((f) => this.touchBuyerField(f))
          if (this.hasBuyerErrors) {
            // Go back to buyer info step
            this.modalState = 'BUYER_INFO'
            return
          }
        } else if (this.bulkMode === 'PER_BILL') {
          const bulkValidation = this.validateBulkGrid()
          if (!bulkValidation.valid) {
            this.modalState = 'BUYER_INFO'
            this.focusBulkGridError(bulkValidation.firstInvalidRow)
            return
          }
        } else if (this.bulkMode === 'SHARED') {
          const sharedValidation = this.validateSharedRowsBeforeSubmit()
          if (!sharedValidation.valid) {
            this.modalState = 'BUYER_INFO'
            this.notifySharedRowsValidationFailure(sharedValidation)
            return
          }
        }

        this.modalState = 'VALIDATING'
        const validationError = this.validateLocal()
        if (validationError) {
          this.resultErrorMessage = validationError
          this.modalState = 'ERROR'
          return
        }
        this.modalState = 'SUBMITTING'
        this.isCreateActionLocked = true
        await this.submit()
      },

      validateSharedRowsBeforeSubmit() {
        const rows = this.buildDraftRows()
        const isWholesale = this.operation.laneSaleType === EINVOICE_SALE_TYPE.WHOLESALE

        for (const row of rows) {
          const buyerType = row.buyerType || this.buyerType || EINVOICE_BUYER_TYPE.ENTERPRISE
          const validation = validateBuyerInfo(
            buyerType,
            {
              customerName: row.customerName || '',
              customerTaxCode: row.customerTaxCode || '',
              customerAddress: row.customerAddress || '',
              customerEmail: row.customerEmail || '',
              customerPhone: row.customerPhone || '',
              customerIdNumber: row.customerIdNumber || '',
            },
            { isWholesale },
          )

          if (!validation.valid) {
            return {
              valid: false,
              billId: row.billId,
              billCode: row.billCode,
              errors: validation.errors,
            }
          }
        }

        return { valid: true }
      },

      getBuyerValidationFieldLabel(field) {
        const labels = {
          customerName: 'Tên người mua',
          customerTaxCode: 'MST người mua',
          customerAddress: 'Địa chỉ',
          customerEmail: 'Email nhận hóa đơn',
          customerPhone: 'Số điện thoại',
          customerIdNumber: 'Số CCCD/CMND',
        }
        return labels[field] || field || 'Thông tin người mua'
      },

      notifySharedRowsValidationFailure(validation = {}) {
        const billLabel = validation.billCode || validation.billId || 'hóa đơn'
        const firstField = Object.keys(validation.errors || {})[0]
        const fieldLabel = this.getBuyerValidationFieldLabel(firstField)
        const fieldMessage = validation.errors?.[firstField]
        const detail = fieldMessage ? `: ${fieldMessage}` : ''
        makeToastWarning(
          `${fieldLabel} chưa hợp lệ ở ${billLabel}${detail}. Vui lòng sửa hoặc xóa giá trị trước khi xem trước VAT.`,
        )
      },

      validateLocal() {
        if (!this.operation.billIds || this.operation.billIds.length === 0) {
          return 'Vui lòng chọn ít nhất 1 hóa đơn'
        }
        if (!SUPPORTED_PROVIDER_MODES.includes(this.operation.providerMode)) {
          return `Provider mode không hợp lệ: ${this.operation.providerMode}`
        }
        if (!this.operation.laneSaleType) {
          return 'Thiếu lane e-invoice hợp lệ'
        }
        return null
      },

      buildDraftRows() {
        // contactPersonName chỉ có giá trị khi user explicit nhập ở ô "Người mua hàng"
        // (chỉ form ENTERPRISE có ô đó → this.buyer.buyerName). INDIVIDUAL không có ô
        // → this.buyer.buyerName luôn rỗng → FAST cột Buyer rỗng. KHÔNG fallback gì cả.
        if (!this.isBulkBatch) {
          return [
            {
              billId: this.operation.billIds[0],
              buyerType: this.buyerType,
              customerName: this.buyer.customerName,
              customerEmail: this.buyer.customerEmail,
              customerPhone: this.buyer.customerPhone,
              customerTaxCode: this.buyer.customerTaxCode,
              customerAddress: this.buyer.customerAddress,
              customerIdNumber: this.buyer.customerIdNumber,
              contactPersonName: this.buyer.buyerName,
            },
          ]
        }

        if (this.bulkMode === 'SHARED') {
          const sourceRows = materializeSharedOverridesToRows(this.gridRows, {
            buyerType: this.buyerType,
            overrides: this.fieldOverrides,
            preserveDirty: true,
          })

          // "Người mua hàng" (buyerContactPerson) không thuộc grid field nên không được
          // materialize vào row — gán contactPersonName dùng chung từ override để gửi lên FAST.
          const contactOverride = this.fieldOverrides && this.fieldOverrides.buyerContactPerson
          const sharedContactPersonName =
            contactOverride && contactOverride.action === FIELD_ACTION.OVERRIDE
              ? contactOverride.value || ''
              : ''

          return sourceRows.map((row) => ({
            ...normalizeDraftRowForPayload(row),
            contactPersonName: sharedContactPersonName,
          }))
        }

        return this.gridRows.map(normalizeDraftRowForPayload)
      },

      buildDraftBatchRequest() {
        const rows = this.buildDraftRows()
        const overrides = rows.reduce((acc, row) => {
          if (!row.billId) {
            return acc
          }

          const override = buildDraftOverride(row)
          if (Object.keys(override).length > 0) {
            acc[row.billId] = override
          }

          return acc
        }, {})

        return {
          billIds: this.operation.billIds,
          overrides,
        }
      },

      async submit() {
        try {
          this.resultErrorMessage = ''
          const res = await this.$store.dispatch(
            EINVOICE_CREATE_DRAFT_BATCH,
            this.buildDraftBatchRequest(),
          )
          const firstDraftId = res?.details?.[0]?.id || res?.draftIds?.[0] || null
          this.$emit('submitted', {
            billIds: this.operation.billIds,
            draftId: firstDraftId,
            draftIds: Array.isArray(res?.draftIds) ? res.draftIds : [],
          })
          this.applyResult(res)
          this.modalState = 'RESULT_SYNC'
        } catch (error) {
          this.resultErrorMessage =
            error?.message || error?.response?.data?.message || 'Không thể tạo nháp HĐĐT'
          makeToastFailure(this.resultErrorMessage)
          this.modalState = 'ERROR'
        } finally {
          this.isCreateActionLocked = false
        }
      },

      validateBulkGrid() {
        const validation = validateGrid(this.gridRows)
        const firstInvalidRow =
          validation.rows.find((row) => row.status !== ROW_STATUS.VALID) || null

        return {
          ...validation,
          valid: !firstInvalidRow,
          firstInvalidRow,
        }
      },

      focusBulkGridError(rowValidation) {
        if (!rowValidation?.billId) {
          return
        }

        this.$nextTick(() => {
          const grid = this.$refs.bulkGrid
          if (!grid) {
            return
          }

          const firstErrorField = Object.keys(rowValidation.errors || {})[0] || 'buyerName'
          grid.revealAndFocusCell(rowValidation.billId, firstErrorField)
        })
      },

      applyResult(res) {
        // Handle both { items: [] } and { errors: [], draftIds: [], details: [] } formats
        const data = res.items ? res : res
        const { ready, needsInfo, failed, activeTab } = bucketizeItems(data)
        this.result = { ready, needsInfo, failed }
        this.activeTab = this.resolveActiveTab(activeTab)
        this.$emit('finished', this.buildSummary())
      },

      resolveActiveTab(legacyActiveTab = 0) {
        if (this.result.failed.length) return 'failed'
        if (this.result.needsInfo.length) return 'needs_info'
        if (this.resultCreated.length) return 'created'
        if (this.resultExisting.length) return 'existing'
        return legacyActiveTab === 2 ? 'failed' : legacyActiveTab === 1 ? 'needs_info' : 'created'
      },

      getResultItemLabel(item = {}) {
        return item.billNumber || item.billCode || (item.draftId ? `Nháp #${item.draftId}` : item.billId)
      },

      canOpenSourceBill(item = {}) {
        return Boolean(item.billNumber)
      },

      retryCreate() {
        if (this.modalState !== 'ERROR') {
          return
        }
        this.resultErrorMessage = ''
        this.modalState = 'VAT_PREVIEW'
      },

      // ── Polling ────────────────────────────────────────────────
      startPolling() {
        const tick = async () => {
          const elapsed = Date.now() - this.jobPollStart
          const interval = computePollingInterval(elapsed)

          try {
            const status = await this.$store.dispatch(EINVOICE_POLL_JOB, this.jobId)
            this.jobProgress = status

            if (['completed', 'completed_with_errors', 'failed'].includes(status.status)) {
              this.stopPolling()
              if (status.status === 'failed') {
                this.resultErrorMessage =
                  status?.message || 'Không thể tạo nháp HĐĐT. Vui lòng thử lại.'
                this.modalState = 'ERROR'
                return
              }
              this.applyResult(status.result)
              this.modalState = 'RESULT_SYNC'
              return
            }
          } catch (err) {
            const code = err?.response?.status
            if (code === 404 || code === 410) {
              this.stopPolling()
              this.jobExpired = true
              this.$emit('finished', this.buildSummary())
              return
            }
          }

          if (elapsed > MAX_POLL_MS) {
            this.stopPolling()
            this.jobTimeout = true
            this.$emit('finished', this.buildSummary())
            return
          }

          this.jobPollTimer = setTimeout(tick, interval)
        }
        tick()
      },

      stopPolling() {
        if (this.jobPollTimer) {
          clearTimeout(this.jobPollTimer)
          this.jobPollTimer = null
        }
      },

      // ── Navigation ─────────────────────────────────────────────
      openDraft(item) {
        const salesInvoiceId = item?.billId || item?.saleInvoiceId || item?.draftId
        if (!salesInvoiceId) return
        // this.$router.push({ name: 'einvoice-detail', params: { salesInvoiceId } });
        // this.$refs.modal.hide();

        // 1. Resolve route để lấy href (URL đầy đủ)
        const routeData = this.$router.resolve({
          name: 'einvoice-detail',
          params: { salesInvoiceId },
          query: { saleType: this.operation.laneSaleType },
        })

        // 2. Mở URL trong tab mới
        window.open(routeData.href, '_blank')

        // // 3. Đóng modal (giữ nguyên logic cũ)
        // this.$refs.modal.hide();
      },

      openDraftList() {
        const ids = [
          ...this.result.ready.map((r) => r.billId),
          ...this.result.needsInfo.map((r) => r.billId),
        ]
          .filter(Boolean)
          .join(',')
        this.$router.push({
          name: 'einvoice-list',
          query: { tab: 'drafts', createdFrom: 'bills', ids },
        })
        this.$refs.modal.hide()
      },

      exportReport() {
        makeToastWarning('Chức năng xuất báo cáo đang phát triển')
      },

      // ── Modal lifecycle ────────────────────────────────────────
      onModalHide(bvModalEvt) {
        if (this.modalState === 'SUBMITTING') {
          bvModalEvt.preventDefault()
          return
        }
        if (this.modalState === 'RESULT_ASYNC') {
          makeToastFailure('Job vẫn đang xử lý nền. Vào Danh sách nháp để xem kết quả sau.')
        }
      },

      onModalHidden() {
        this.stopPolling()
        this.$emit('closed')
      },

      navigateToGridError(item) {
        this.modalState = 'BUYER_INFO'
        this.activeTab = 'needs_info'
        this.$nextTick(() => {
          const grid = this.$refs.bulkGrid
          if (grid && item.billId) {
            const firstErrorField = item.missingFields?.[0] || 'buyerName'
            grid.revealAndFocusCell(item.billId, firstErrorField)
          }
        })
      },

      openSourceBill(billNumber) {
        if (!billNumber) return
        const routeName =
          this.operation.laneSaleType === EINVOICE_SALE_TYPE.WHOLESALE
            ? 'update-wholesale-invoice'
            : 'update-bill'
        this.$router.push({ name: routeName, query: { id: billNumber } })
        this.$refs.modal.hide()
      },

      reset() {
        this.stopPolling()
        this.modalState = 'IDLE'
        this.operation = {
          billIds: [],
          providerMode: this.defaultProviderMode,
          laneSaleType: normalizeSaleType(this.defaultProviderMode) || EINVOICE_SALE_TYPE.RETAIL,
          billType: null,
          billTypeBySalesInvoiceId: {},
          entryPoint: 'BILL_DETAIL',
          prefer: this.defaultPrefer,
        }
        this.preflightWarnings = []
        this.buyerType = EINVOICE_BUYER_TYPE.ENTERPRISE
        this.resetBuyerFields()
        this.jobId = null
        this.jobPollStart = null
        this.jobProgress = { current: 0, total: 0, status: null }
        this.jobExpired = false
        this.jobTimeout = false
        this.resultErrorMessage = ''
        this.result = { ready: [], needsInfo: [], failed: [] }
        this.activeTab = 'created'
        // Bulk mode state
        this.bulkMode = 'SHARED'
        this.detectedMode = null
        this.fieldOverrides = {}
        this.gridRows = []
        this.affectedBills = []
        this.isFetchingBillInfo = false
        this.billInfoFetchError = null
        this.sharedFormValid = true
        this.isCreateActionLocked = false
        this.fieldOverridesByType = {
          [EINVOICE_BUYER_TYPE.ENTERPRISE]: {},
          [EINVOICE_BUYER_TYPE.INDIVIDUAL]: {},
        }
        this.buyerDraftsByType = createRetailBuyerDrafts()
        this.buyerBootstrapSnapshotMode = 'none'
        this.sourceBillsById = {}
        this.bootstrapSessionToken = 0

        // Clear VAT preview state
        this.$store.dispatch(EINVOICE_CLEAR_PREVIEW)
      },

      buildSummary() {
        return {
          readyCount: this.result.ready.length,
          createdCount: this.resultCreated.length,
          existingCount: this.resultExisting.length,
          needsInfoCount: this.result.needsInfo.length,
          failedCount: this.result.failed.length,
          readyBillIds: this.result.ready.map((r) => r.billId),
          needsInfoBillIds: this.result.needsInfo.map((r) => r.billId),
          jobId: this.jobId,
        }
      },

      getReasonMeta(reasonCode) {
        return getDraftReasonMeta(reasonCode)
      },

      // Error display helpers - handle both API formats
      getErrorLabel(item) {
        // Use direct error message if available, fallback to reason code lookup
        if (item.errorMessage) {
          return this.truncateErrorMessage(item.errorMessage)
        }
        const meta = this.getReasonMeta(item.reasonCode)
        return meta.label || item.reasonCode || 'Lỗi không xác định'
      },

      getErrorDescription(item) {
        // Use direct error message for description
        if (item.errorMessage) {
          return item.errorMessage
        }
        const meta = this.getReasonMeta(item.reasonCode)
        return meta.description || item.message || 'Không có mô tả chi tiết.'
      },

      getErrorHowToFix(item) {
        // For database errors, provide generic guidance
        if (item.errorMessage) {
          if (
            item.errorMessage.includes('Data too long') ||
            item.errorMessage.includes('Data truncated')
          ) {
            return 'Dữ liệu hóa đơn có trường vượt quá giới hạn. Vui lòng kiểm tra và sửa dữ liệu gốc, hoặc liên hệ hỗ trợ kỹ thuật.'
          }
          return 'Vui lòng kiểm tra dữ liệu hóa đơn gốc hoặc liên hệ hỗ trợ kỹ thuật.'
        }
        const meta = this.getReasonMeta(item.reasonCode)
        return meta.howToFix || null
      },

      truncateErrorMessage(msg, maxLen = 50) {
        if (!msg) return ''
        if (msg.length <= maxLen) return msg
        return msg.substring(0, maxLen) + '…'
      },

      // VAT Preview event handlers
      onPreviewLoaded(data) {
        this.$emit('preview-loaded', data)
      },

      onPreviewError(error) {
        this.$emit('preview-error', error)
      },
    },
  }
</script>

<style lang="scss" scoped>
  /* ── Design Tokens (inherited from einvoice.scss) ──────────── */
$draft-primary: #3699ff;
$draft-success: #1bc5bd;
$draft-warning: #ffa800;
$draft-danger: #f64e60;
$draft-bg: #f3f6f9;
$draft-border: #ebedf3;
$draft-text: #3f4254;
$draft-text-muted: #b5b5c3;
$draft-text-label: #6c757d;
$draft-radius: 0.375rem;

/* ── Modal Overrides ───────────────────────────────────────── */
.draft-modal-header {
  padding: 0;
}

.draft-modal-title {
  // font-size: 1rem;
  font-weight: 600;
  color: $draft-text;

  i {
    color: $draft-primary;
  }
}

/* ── Stepper ───────────────────────────────────────────────── */
.draft-stepper {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.75rem 0 0;
}

.draft-step {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  position: relative;

  &:last-child {
    flex: 0 100px;
  }
}

.step-indicator {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  // font-size: 0.72rem;
  font-weight: 600;
  flex-shrink: 0;
  transition: all 0.25s ease;
  background: $draft-border;
  color: $draft-text-muted;
}

.step-label {
  // font-size: 0.72rem;
  font-weight: 500;
  margin-left: 0.4rem;
  white-space: nowrap;
  color: $draft-text-muted;
  transition: color 0.25s ease;
}

.step-connector {
  flex: 1;
  height: 2px;
  background: $draft-border;
  margin: 0 0.75rem;
  transition: background 0.25s ease;
}

.draft-step.is-completed {
  .step-indicator {
    background: $draft-success;
    color: #fff;
    i {
       color: #fff;
    }
  }
  .step-label {
    color: $draft-success;
  }
  .step-connector {
    background: $draft-success;
  }
}

.draft-step.is-active {
  .step-indicator {
    background: $draft-primary;
    color: #fff;
    box-shadow: 0 0 0 4px rgba($draft-primary, 0.15);
    i {
       color: #fff;
    }
  }
  .step-label {
    color: $draft-primary;
    font-weight: 600;
  }
}

/* ── Step Content ──────────────────────────────────────────── */
.draft-step-content {
  min-height: 200px;
}

/* ── Confirm: Summary Bar ───────────────────────────────────── */
.confirm-summary-bar {
  display: flex;
  flex-direction: column;
  gap: 0;
  background: $draft-bg;
  border: 1px solid $draft-border;
  border-radius: $draft-radius;
  overflow: hidden;
}

.summary-row-basic {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 0.75rem 1rem;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;

  i {
    // font-size: 1.1rem;
  }

  div {
    display: flex;
    flex-direction: column;
  }
}

.summary-value {
  // font-size: 1rem;
  font-weight: 700;
  color: $draft-text;
  line-height: 1.2;
}

.summary-label {
  // font-size: 0.72rem;
  color: $draft-text-label;
  line-height: 1.3;
}

.summary-divider {
  width: 1px;
  height: 32px;
  background: $draft-border;
  margin: 0 1.25rem;
}

/* ── Confirm: Bills List ───────────────────────────────────── */
.section-label {
  display: block;
  // font-size: 0.78rem;
  font-weight: 600;
  color: $draft-text-label;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.5rem;
}

.bills-scroll-area {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid $draft-border;
  border-radius: $draft-radius;
}

.bill-preview-item {
  display: flex;
  align-items: center;
  padding: 0.45rem 0.75rem;
  border-bottom: 1px solid $draft-border;
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: $draft-bg;
  }
}

.bill-index {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: $draft-bg;
  display: flex;
  align-items: center;
  justify-content: center;
  // font-size: 0.68rem;
  font-weight: 600;
  color: $draft-text-label;
  margin-right: 0.6rem;
  flex-shrink: 0;
}

.bill-code {
  // font-size: 0.85rem;
  font-weight: 500;
  color: $draft-primary;
  font-variant-numeric: tabular-nums;
}

.bill-code-link {
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid $draft-primary;
    outline-offset: 2px;
    border-radius: 2px;
  }
}

.bill-preview-more {
  padding: 0.5rem 0.75rem;
  // font-size: 0.78rem;
  color: $draft-text-muted;
  text-align: center;
  background: $draft-bg;
}

/* ── Confirm: Warnings ─────────────────────────────────────── */
.confirm-warnings {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.warning-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: $draft-radius;
  // font-size: 0.82rem;

  &.severity-hard {
    background: #fff5f8;
    color: $draft-danger;
  }

  &.severity-soft {
    background: #fff8dd;
    color: darken($draft-warning, 15%);
  }

  i {
    margin-top: 2px;
    flex-shrink: 0;
  }
}

/* ── Processing State ──────────────────────────────────────── */
.processing-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 0;
}

.processing-visual {
  text-align: center;
}

.processing-icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba($draft-primary, 0.08);
}

.processing-title {
  // font-size: 0.95rem;
  font-weight: 600;
  color: $draft-text;
}

.processing-subtitle {
  // font-size: 0.8rem;
  color: $draft-text-label;
  max-width: 360px;
  margin: 0 auto;
}

.processing-progress {
  width: 100%;
  max-width: 400px;
  margin-top: 1.25rem;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  // font-size: 0.75rem;
  color: $draft-text-label;
}

.progress-count {
  font-weight: 600;
  color: $draft-primary;
  font-variant-numeric: tabular-nums;
}

.progress-percent {
  text-align: right;
  // font-size: 0.72rem;
  font-weight: 600;
  color: $draft-primary;
}

.processing-hint {
  margin-top: 1.25rem;
  padding: 0.5rem 0.75rem;
  background: #eee5ff;
  border-radius: $draft-radius;
  // font-size: 0.75rem;
  color: #8950fc;
  text-align: center;

  i {
    color: #8950fc;
  }
}

/* ── Result State ──────────────────────────────────────────── */
.result-alert {
  // font-size: 0.82rem;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.75rem;
}

.result-overview {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-radius: $draft-radius;
  margin-bottom: 0.75rem;
  border: 1px solid $draft-border;
  background: $draft-bg;
}

.result-overview__icon {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.result-overview__eyebrow {
  // font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.15rem;
}

.result-overview__title {
  // font-size: 0.95rem;
  font-weight: 700;
  color: $draft-text;
}

.result-overview__subtitle {
  // font-size: 0.8rem;
  line-height: 1.55;
  color: $draft-text-label;
}

.result-overview--success {
  background: #ecfdf8;
  border-color: rgba($draft-success, 0.2);

  .result-overview__icon {
    background: rgba($draft-success, 0.14);
    color: darken($draft-success, 10%);
  }

  .result-overview__eyebrow {
    color: darken($draft-success, 10%);
  }
}

.result-overview--info {
  background: #eef6ff;
  border-color: rgba($draft-primary, 0.18);

  .result-overview__icon {
    background: rgba($draft-primary, 0.14);
    color: darken($draft-primary, 8%);
  }

  .result-overview__eyebrow {
    color: darken($draft-primary, 8%);
  }
}

.result-overview--warning {
  background: #fff9eb;
  border-color: rgba($draft-warning, 0.25);

  .result-overview__icon {
    background: rgba($draft-warning, 0.16);
    color: darken($draft-warning, 12%);
  }

  .result-overview__eyebrow {
    color: darken($draft-warning, 12%);
  }
}

.result-overview--danger {
  background: #fff5f7;
  border-color: rgba($draft-danger, 0.22);

  .result-overview__icon {
    background: rgba($draft-danger, 0.13);
    color: darken($draft-danger, 5%);
  }

  .result-overview__eyebrow {
    color: darken($draft-danger, 5%);
  }
}

/* ── Result: Summary Cards ─────────────────────────────────── */
.result-summary-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.result-card {
  flex: 1 1 180px;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.65rem 0.75rem;
  border-radius: $draft-radius;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    transform: translateY(-1px);
  }

  &:focus {
    outline: 2px solid $draft-primary;
    outline-offset: 2px;
  }
}

.result-card--success {
  background: #c9f7f5;
  .result-card-icon i {
    color: $draft-success;
  }
  .result-card-count {
    color: darken($draft-success, 10%);
  }
  &.is-active {
    border-color: $draft-success;
  }
}

.result-card--warning {
  background: #fff8dd;
  .result-card-icon i {
    color: $draft-warning;
  }
  .result-card-count {
    color: darken($draft-warning, 10%);
  }
  &.is-active {
    border-color: $draft-warning;
  }
}

.result-card--info {
  background: #eef6ff;
  .result-card-icon i {
    color: $draft-primary;
  }
  .result-card-count {
    color: darken($draft-primary, 8%);
  }
  &.is-active {
    border-color: $draft-primary;
  }
}

.result-card--danger {
  background: #fff5f8;
  .result-card-icon i {
    color: $draft-danger;
  }
  .result-card-count {
    color: darken($draft-danger, 5%);
  }
  &.is-active {
    border-color: $draft-danger;
  }
}

.result-card-icon {
  // font-size: 1.2rem;
}

.result-card-count {
  // font-size: 1.15rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

.result-card-label {
  // font-size: 0.7rem;
  color: $draft-text-label;
  display: block;
  line-height: 1.2;
}

/* ── Result: Items List ────────────────────────────────────── */
.result-tab-content {
  border: 1px solid $draft-border;
  border-radius: $draft-radius;
  max-height: 340px;
  overflow-y: auto;
}

.result-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  color: $draft-text-muted;
  // font-size: 0.85rem;
  gap: 0.5rem;

  i {
    // font-size: 1.5rem;
  }
}

.result-items-list {
  display: flex;
  flex-direction: column;
}

.result-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid $draft-border;
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: $draft-bg;
  }
}

.result-item--info {
  .result-item-code {
    color: darken($draft-primary, 8%);
  }
}

.result-item-main {
  flex: 1;
  min-width: 0;
}

.result-item-code {
  display: flex;
  align-items: center;
  // font-size: 0.85rem;

  i {
    flex-shrink: 0;
  }
}

.result-item-meta {
  // font-size: 0.75rem;
  color: $draft-text-label;
  margin-top: 2px;
  padding-left: 1.2rem;

  i {
    // font-size: 0.65rem;
  }
}

.result-item-missing {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 4px;
  padding-left: 1.2rem;
}

.missing-tag {
  display: inline-block;
  padding: 1px 6px;
  background: #fff8dd;
  color: darken($draft-warning, 15%);
  border-radius: 3px;
  // font-size: 0.68rem;
  font-weight: 500;
}

.result-item-reason {
  margin-top: 2px;
  padding-left: 1.2rem;
  max-width: 100%;
  overflow: hidden;
}

.reason-label {
  // font-size: 0.75rem;
  color: $draft-danger;
  font-weight: 500;
  word-break: break-word;
  display: block;
  line-height: 1.4;
}

/* ── Buyer Info Section ────────────────────────────────────── */
.buyer-info-section {
  border: 1px solid $draft-border;
  border-radius: $draft-radius;
  padding: 0.75rem;
  background: #fff;
}

.buyer-type-toggle {
  display: flex;
  gap: 0;
  margin-bottom: 0.75rem;
  border: 1px solid $draft-border;
  border-radius: $draft-radius;
  overflow: hidden;
}

.buyer-type-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.45rem 0.75rem;
  border: none;
  background: $draft-bg;
  color: $draft-text-label;
  // font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:not(:last-child) {
    border-right: 1px solid $draft-border;
  }

  &:hover:not(.is-active) {
    background: darken($draft-bg, 3%);
  }

  &.is-active {
    background: $draft-primary;
    color: #fff;
    font-weight: 600;

    i {
      color: #fff;
    }
  }

  i {
    // font-size: 0.75rem;
    color: $draft-text-muted;
  }
}

.skip-invoice-row {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.6rem;
  background: $draft-bg;
  border-radius: $draft-radius;
  margin-bottom: 0.75rem;
  // font-size: 0.8rem;
}

.buyer-form {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.buyer-form-row {
  display: flex;
  gap: 0.75rem;

  &--2col {
    > .buyer-field {
      flex: 1;
      min-width: 0;
    }
  }
}

.buyer-field {
  flex: 1;
  margin-bottom: 0.35rem;

  ::v-deep .col-form-label {
    // font-size: 0.75rem;
    font-weight: 600;
    color: $draft-text-label;
    padding-bottom: 0.15rem;
  }

  ::v-deep .form-control-sm {
    // font-size: 0.82rem;
  }

  ::v-deep .invalid-feedback {
    // font-size: 0.72rem;
  }
}

.tax-lookup-btn {
  white-space: nowrap;
  flex-shrink: 0;
  // font-size: 0.78rem;
}

.tax-lookup-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.6rem;
  border-radius: $draft-radius;
  // font-size: 0.78rem;
  margin-bottom: 0.6rem;

  i {
    flex-shrink: 0;
  }

  &--success {
    background: #c9f7f5;
    color: darken($draft-success, 15%);
  }

  &--warning {
    background: #fff8dd;
    color: darken($draft-warning, 15%);
  }

  &--danger {
    background: #fff5f8;
    color: $draft-danger;
  }
}

/* ── Footer ────────────────────────────────────────────────── */
.draft-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.draft-footer-meta {
  flex: 1 1 100%;
  order: -1;
  // font-size: 0.78rem;
  line-height: 1.5;
}

/* ── Error Popover ─────────────────────────────────────────── */
.popover-fix-guide {
  // font-size: 0.78rem;
  padding: 0.4rem 0.5rem;
  background: $draft-bg;
  border-radius: 3px;
  line-height: 1.5;

  strong {
    display: block;
    margin-bottom: 2px;
    color: $draft-text;
  }
}

@media (max-width: 991.98px) {
  .einvoice-draft-modal {
    .modal-header,
    .modal-body,
    .modal-footer {
      padding-left: 1rem;
      padding-right: 1rem;
    }
  }
}

@media (max-width: 767.98px) {
  .result-overview {
    flex-direction: column;
  }

  .draft-step {
    flex: 1 1 100%;
  }

  .step-label {
    white-space: normal;
    line-height: 1.35;
  }

  .step-connector {
    display: none;
  }

  .summary-divider {
    display: none;
  }

  .summary-row-basic {
    align-items: flex-start;
  }

  .draft-footer {
    align-items: stretch;
  }

  .draft-footer .btn,
  .draft-footer .btn-group {
    width: 100%;
  }

  .result-summary-cards {
    flex-direction: column;
  }

  .result-tab-content {
    max-height: none;
  }
}
</style>

<style lang="scss">
  /* ── Unscoped: Modal-level overrides ───────────────────────── */
.einvoice-draft-modal {
  .modal-header {
    padding: 1rem 1.25rem 0;
    border-bottom: none;
  }

  .modal-body {
    padding: 1rem 1.25rem;
  }

  .modal-footer {
    padding: 0.75rem 1.25rem;
    border-top: 1px solid #ebedf3;
  }

  .draft-error-popover {
    max-width: 320px;

    .popover-header {
      // font-size: 0.82rem;
      font-weight: 600;
    }

    .popover-body {
      // font-size: 0.78rem;
      line-height: 1.6;
    }
  }
}
</style>
